import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTeachingMode } from "../contexts/TeachingModeContext";
import { useClearChat } from "../contexts/ClearChatContext";
import {
  FaPaperclip,
  FaImage,
  FaCamera,
  FaPlusCircle,
  FaEdit,
} from "react-icons/fa";

import { FaTimes } from "react-icons/fa";
import { FaClone, FaThumbsUp, FaThumbsDown, FaShareAlt } from "react-icons/fa";

import { FaPencil } from "react-icons/fa6";

import { FaBook } from "react-icons/fa6";

import { MdGraphicEq } from "react-icons/md";

import MathJaxRenderer from "./MathJaxRenderer";
import ShareButton from "./ShareButton";

import SimpleThreeDotsLoader from "./SimpleThreeDotsLoader";
import "../styles/MobileChatUI.css";

const MobileCameraModal = ({
  isOpen,
  onClose,
  onCapture,
  hideCapture = false,
  silenceTimerActive = false,
  silenceTimerCount = 5,
  triggerCapture = false,
  onAutoCapture = null,
  handleSend,
}) => {
  const videoRef = useRef(null);

  const canvasRef = useRef(null);

  const modalRef = useRef(null);

  const [stream, setStream] = useState(null);

  const [error, setError] = useState(null);

  const [isMinimized, setIsMinimized] = useState(false);

  const [isDragging, setIsDragging] = useState(false);

  const [position, setPosition] = useState({ x: 24, y: 24 });

  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const [touchOffset, setTouchOffset] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (isOpen) {
      startCamera();
    } else {
      stopCamera();
    }

    return () => stopCamera();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Trigger capture when triggerCapture prop changes
  useEffect(() => {
    if (triggerCapture && stream && videoRef.current) {
      console.log(
        "[Silence Timer] MobileCameraModal received trigger, capturing photo",
      );
      capturePhoto();
      handleSend();
    }
  }, [triggerCapture, stream]);

  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e) => {
        const newX = e.clientX - dragOffset.x;

        const newY = e.clientY - dragOffset.y;

        setPosition((prev) => ({
          x: Math.max(
            0,
            Math.min(newX, window.innerWidth - (isMinimized ? 200 : 340)),
          ),

          y: Math.max(
            0,
            Math.min(newY, window.innerHeight - (isMinimized ? 60 : 300)),
          ),
        }));
      };

      const handleMouseUp = () => setIsDragging(false);

      const handleTouchMove = (e) => {
        if (!e.touches.length) return;

        const touch = e.touches[0];

        const newX = touch.clientX - touchOffset.x;

        const newY = touch.clientY - touchOffset.y;

        setPosition((prev) => ({
          x: Math.max(
            0,
            Math.min(newX, window.innerWidth - (isMinimized ? 200 : 340)),
          ),

          y: Math.max(
            0,
            Math.min(newY, window.innerHeight - (isMinimized ? 60 : 300)),
          ),
        }));
      };

      const handleTouchEnd = () => setIsDragging(false);

      document.addEventListener("mousemove", handleMouseMove);

      document.addEventListener("mouseup", handleMouseUp);

      document.addEventListener("touchmove", handleTouchMove);

      document.addEventListener("touchend", handleTouchEnd);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);

        document.removeEventListener("mouseup", handleMouseUp);

        document.removeEventListener("touchmove", handleTouchMove);

        document.removeEventListener("touchend", handleTouchEnd);
      };
    }
  }, [isDragging, dragOffset, touchOffset, isMinimized]);

  const startCamera = async () => {
    try {
      if (
        typeof navigator === "undefined" ||
        !navigator.mediaDevices?.getUserMedia
      ) {
        setError("Camera not supported on this device/browser.");

        return;
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },

          width: { ideal: 1280 },

          height: { ideal: 720 },
        },

        audio: false,
      });

      setStream(mediaStream);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;

        try {
          await videoRef.current.play();
        } catch (_) {}
      }

      setError(null);
    } catch (err) {
      setError(err?.message || "Unable to access camera.");
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());

      setStream(null);
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;

    const canvas = canvasRef.current;

    const context = canvas.getContext("2d");

    canvas.width = video.videoWidth;

    canvas.height = video.videoHeight;

    context.setTransform(1, 0, 0, 1, 0, 0);

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (blob) {
          const file = new File([blob], `camera-capture-${Date.now()}.jpg`, {
            type: "image/jpeg",
          });

          // Use auto-capture handler if this is triggered by timer
          if (triggerCapture && onAutoCapture) {
            onAutoCapture(file);
          } else if (onCapture) {
            onCapture(file);
          }

          onClose?.();
        }
      },
      "image/jpeg",
      0.9,
    );
  };

  const handlePointerDown = (clientX, clientY, isTouch = false) => {
    if (!modalRef.current) return;

    const rect = modalRef.current.getBoundingClientRect();

    if (isTouch) {
      setTouchOffset({ x: clientX - rect.left, y: clientY - rect.top });
    } else {
      setDragOffset({ x: clientX - rect.left, y: clientY - rect.top });
    }

    setIsDragging(true);
  };

  if (!isOpen) return null;

  return (
    <div
      data-camera-modal
      ref={modalRef}
      onMouseDown={(e) => {
        if (e.target.closest(".mobile-camera-modal-header")) {
          handlePointerDown(e.clientX, e.clientY, false);
        }
      }}
      onTouchStart={(e) => {
        if (
          e.target.closest(".mobile-camera-modal-header") &&
          e.touches.length
        ) {
          const touch = e.touches[0];

          handlePointerDown(touch.clientX, touch.clientY, true);
        }
      }}
      style={{
        position: "fixed",

        top: position.y,

        left: position.x,

        width: isMinimized ? 200 : "90vw",

        maxWidth: 340,

        height: isMinimized ? 60 : "auto",

        zIndex: 2147483647,

        background: "white",

        borderRadius: 12,

        boxShadow: "0 4px 24px rgba(0,0,0,0.15)",

        border: "1px solid #e5e7eb",

        display: "flex",

        flexDirection: "column",

        overflow: "hidden",

        cursor: isDragging ? "grabbing" : "default",

        transition: isDragging ? "none" : "all 0.3s ease",
      }}
    >
      <div
        className="mobile-camera-modal-header"
        style={{
          display: "flex",

          alignItems: "center",

          justifyContent: "space-between",

          padding: "12px 16px",

          backgroundColor: "#f8fafc",

          borderBottom: isMinimized ? "none" : "1px solid #e5e7eb",

          cursor: "grab",

          userSelect: "none",
        }}
      >
        <h3
          className="text-sm font-semibold"
          style={{
            margin: 0,
            color: "#374151",
            fontSize: isMinimized ? "12px" : "14px",
          }}
        >
          📹 Interactive Learning
        </h3>

        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <button
            onClick={() => setIsMinimized((prev) => !prev)}
            style={{
              background: "none",

              border: "none",

              fontSize: "14px",

              cursor: "pointer",

              color: "#6b7280",

              padding: "4px",

              borderRadius: "4px",

              display: "flex",

              alignItems: "center",

              justifyContent: "center",

              width: "20px",

              height: "20px",
            }}
            aria-label={isMinimized ? "Maximize" : "Minimize"}
            title={isMinimized ? "Maximize" : "Minimize"}
          >
            {isMinimized ? "🔄" : "➖"}
          </button>

          <button
            onClick={() => {
              onClose?.();

              setIsMinimized(false);
            }}
            style={{
              background: "none",

              border: "none",

              fontSize: "16px",

              cursor: "pointer",

              color: "#ef4444",

              padding: "4px",

              borderRadius: "4px",

              display: "flex",

              alignItems: "center",

              justifyContent: "center",

              width: "20px",

              height: "20px",
            }}
            aria-label="Close camera"
            title="Close"
          >
            ×
          </button>
        </div>
      </div>

      {!isMinimized && (
        <div style={{ padding: "16px" }}>
          {error ? (
            <div className="text-center py-8">
              <p className="text-red-500 mb-4" style={{ fontSize: "14px" }}>
                {error}
              </p>

              <button
                onClick={startCamera}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                style={{ fontSize: "14px" }}
              >
                Try Again
              </button>
            </div>
          ) : (
            <div className="text-center w-full">
              <video
                data-role="live-camera"
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full max-h-60 rounded-lg mb-4"
                style={{
                  maxHeight: "200px",
                  transform: "scaleX(-1)",
                  transformOrigin: "center",
                }}
              />

              <canvas ref={canvasRef} style={{ display: "none" }} />

              {/* Silence Timer UI */}
              {silenceTimerActive && (
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    background: "rgba(0, 0, 0, 0.8)",
                    color: "white",
                    padding: "16px 24px",
                    borderRadius: "12px",
                    fontSize: "18px",
                    fontWeight: "bold",
                    zIndex: 10,
                    textAlign: "center",
                    minWidth: "120px",
                  }}
                >
                  <div style={{ fontSize: "14px", marginBottom: "8px" }}>
                    No speech detected
                  </div>
                  <div style={{ fontSize: "24px", color: "#fbbf24" }}>
                    {silenceTimerCount}
                  </div>
                  <div
                    style={{ fontSize: "12px", marginTop: "4px", opacity: 0.8 }}
                  >
                    Auto-capturing in...
                  </div>
                </div>
              )}

              {stream && !hideCapture && (
                <button
                  data-capture-button
                  onClick={capturePhoto}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  style={{ fontSize: "14px", marginTop: "8px" }}
                >
                  📸 Capture Photo
                </button>
              )}

              {stream && hideCapture && (
                <p style={{ fontSize: "14px", color: "#374151" }}>
                  Live camera mode active. Point at your problem to get help.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const MobileChatUI = ({
  // Layout

  asModal = false,

  title = "Chat",

  onClose,

  paddingClassName = "p-2 sm:p-4",

  messageContainerMaxHeight = "calc(100vh - 200px)",

  // Intro + messages

  showIntro = false,

  introContent = null,

  messages = [],

  messagesContainerRef,

  chatEndRef,

  applyBasicMarkdown,

  workbookMode,

  // Loading

  showProcessingIndicator = false,

  loadingMessage = "Processing...",
  isHistoryLoading = false,

  // Communication panel

  renderCommunicationPanel,

  // Input area

  textareaRef,

  inputText,

  onInputTextChange,

  onKeyDown,

  onPaste,

  isProcessing = false,

  // Voice/Clear controls

  isVoicePaused = false,

  onVoiceClick,

  onResumeListening,

  onClearChat,

  onLiveAssistClick,

  // Voice mode helpers
  communicationMode = false,
  onStopSystemSpeech,

  // Attachments preview

  imagePreview,

  imageName,

  fileName,

  onRemoveImage,

  onClearFile,

  // Attachment actions

  onFileChange,

  onImageChange,

  onCameraClick,

  onCameraCapture,

  cameraModalOpen,

  onCameraModalOpenChange,

  cameraHideCapture = false,

  onCameraVisibilityChange,

  // Timer props
  silenceTimerActive = false,
  silenceTimerCount = 5,
  triggerCameraCapture = false,
  onAutoCapture = null,

  onWorkbookClick,

  resetKey,

  // Actions

  onSend,

  handleSend,

  fromQuestions = false,

  // Modes

  activeMode = "stepbystep",

  onModeChange,

  showModeDropdownOnMobile = true,
  handleWorkbookClick,
}) => {
  const [showAttachMenu, setShowAttachMenu] = useState(false);

  const [showMobileChatActions, setShowMobileChatActions] = useState(false);

  const [showHomeworkTooltip, setShowHomeworkTooltip] = useState(false);

  const [messageReactions, setMessageReactions] = useState({});

  // Toggle like/dislike functionality
  const toggleMessageReaction = (messageId, reaction) => {
    setMessageReactions((prev) => {
      const current = prev[messageId] || { liked: false, disliked: false };

      if (reaction === "like") {
        if (current.liked) {
          // If already liked, remove like
          return { ...prev, [messageId]: { ...current, liked: false } };
        } else {
          // If not liked, add like and remove dislike
          return { ...prev, [messageId]: { liked: true, disliked: false } };
        }
      } else if (reaction === "dislike") {
        if (current.disliked) {
          // If already disliked, remove dislike
          return { ...prev, [messageId]: { ...current, disliked: false } };
        } else {
          // If not disliked, add dislike and remove like
          return { ...prev, [messageId]: { liked: false, disliked: true } };
        }
      }

      return prev;
    });
  };

  const [showStepTooltip, setShowStepTooltip] = useState(false);

  const [showCameraModal, setShowCameraModal] = useState(false);

  const { isWorkbookMode: contextWorkbookMode } = useTeachingMode();
  const { triggerClearChat, hasActiveChat } = useClearChat();
  const effectiveWorkbookMode =
    typeof workbookMode === "boolean" ? workbookMode : contextWorkbookMode;
  const supportsInternalCamera = typeof onCameraCapture === "function";
  const isCameraModalControlled = typeof cameraModalOpen === "boolean";
  const wasProcessingRef = useRef(false);

  useEffect(() => {
    if (isCameraModalControlled) {
      setShowCameraModal(Boolean(cameraModalOpen));
    }
  }, [cameraModalOpen, isCameraModalControlled]);

  useEffect(() => {
    onCameraVisibilityChange?.(showCameraModal);
  }, [showCameraModal, onCameraVisibilityChange]);

  useEffect(() => {
    if (wasProcessingRef.current && !isProcessing) {
      const node = textareaRef?.current;
      if (node && typeof node.focus === "function") {
        node.focus();
      }
    }
    wasProcessingRef.current = isProcessing;
  }, [isProcessing, textareaRef]);

  const handleModeToggle = (mode) => {
    if (onModeChange) onModeChange(mode);

    setShowAttachMenu(false);

    setShowMobileChatActions(false);

    try {
      textareaRef?.current?.focus();
    } catch (_) {}

    setShowHomeworkTooltip(false);

    setShowStepTooltip(false);
  };

  const handleAndCloseAttachMenu = (callback) => {
    setShowAttachMenu(false);

    if (typeof callback === "function") {
      callback();
    }
  };

  const handleSendWithVoiceCleanup = useCallback(() => {
    if (
      effectiveWorkbookMode &&
      communicationMode &&
      typeof onStopSystemSpeech === "function"
    ) {
      onStopSystemSpeech();
    }
    if (typeof onSend === "function") {
      onSend();
    }
  }, [
    communicationMode,
    effectiveWorkbookMode,
    onSend,
    onStopSystemSpeech,
  ]);

  const handleCameraButtonClick = () => {
    if (isProcessing) return;

    if (supportsInternalCamera) {
      setShowAttachMenu(false);

      if (isCameraModalControlled) {
        onCameraModalOpenChange?.(true);
      } else {
        setShowCameraModal(true);
      }
    } else {
      handleAndCloseAttachMenu(onCameraClick);
    }
  };

  const handleCameraCaptureInternal = (file) => {
    if (onCameraCapture) {
      onCameraCapture(file);
    }

    if (isCameraModalControlled) {
      onCameraModalOpenChange?.(false);
    } else {
      setShowCameraModal(false);
    }
  };

  const handleCameraCloseInternal = () => {
    if (isCameraModalControlled) {
      onCameraModalOpenChange?.(false);
    } else {
      setShowCameraModal(false);
    }
  };

  const content = (
    <div className={`h-full flex flex-col ${paddingClassName}`}>
      {showIntro && introContent}

      {!showIntro && (
        <div
          ref={messagesContainerRef}
          className="relative flex-1 overflow-y-auto pr-2 flex flex-col gap-3 min-h-0 custom-scrollbar"
          style={{
            maxHeight: messageContainerMaxHeight,
            scrollbarWidth: "thin",
            scrollbarColor: "#cbd5e0 #f7fafc",
          }}
        >
          {isHistoryLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80">
              <SimpleThreeDotsLoader message="Loading chat history..." />
            </div>
          )}
          {messages.map((msg, index) =>
            msg.type === "bot" ? (
              <div key={`m-bot-${msg.id || msg.timestamp || index}`}>
                <div
                  key={`m-bot-inner-${msg.id || msg.timestamp || index}`}
                  className="flex justify-start"
                >
                  <div className="p-3 whitespace-pre-line rounded-xl max-w-[90%] sm:max-w-[80%] bg-gray-100 text-gray-800 self-start">
                    <style>{`

                    .ai-table-bordered table { border-collapse: collapse; width: 100%; margin-bottom: 1em; }

                    .ai-table-bordered th, .ai-table-bordered td { border: 1px solid #888; padding: 6px 12px; text-align: left; }

                    .ai-table-bordered th { background: #f3f4f6; }

                  `}</style>

                    <div className="ai-table-bordered">
                      {(msg.text || "").includes("<table>") ? (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: (applyBasicMarkdown
                              ? applyBasicMarkdown(msg.text || "")
                              : msg.text || ""
                            ).replace(
                              /(\n)([\u2705\u26a0\u274c\uD83D\uDCCA\uD83D\uDFE2]|\*\*|<table>)/g,
                              "<br>$2",
                            ),
                          }}
                        />
                      ) : (
                        <MathJaxRenderer className="whitespace-pre-line">
                          {(applyBasicMarkdown
                            ? applyBasicMarkdown(msg.text || "")
                            : msg.text || ""
                          )

                            .replace(
                              /(\n)([\u2705\u26a0\u274c\uD83D\uDCCA\uD83D\uDFE2]|\*\*)/g,
                              "\n$2",
                            )

                            .replace(/<br\s*\/?>/gi, "\n")}
                        </MathJaxRenderer>
                      )}
                    </div>
                  </div>
                </div>
                {/* Action icons below the user's message */}
                <div className="mt-1 flex items-center gap-3 justify-start pl-1 text-gray-500 self-start py-2">
                  <button
                    type="button"
                    title="Copy"
                    className="hover:text-gray-700"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(msg.text || "");
                        toast?.success?.("Copied");
                      } catch (e) {
                        console.warn("Copy failed", e);
                      }
                    }}
                  >
                    <FaClone size={14} />
                  </button>
                  <button
                    type="button"
                    title="Like"
                    className={`hover:text-green-600 ${
                      messageReactions[msg.id || msg.timestamp]?.liked
                        ? "text-green-600"
                        : "text-gray-500"
                    }`}
                    onClick={() =>
                      toggleMessageReaction(msg.id || msg.timestamp, "like")
                    }
                  >
                    <FaThumbsUp size={14} />
                  </button>
                  <button
                    type="button"
                    title="Dislike"
                    className={`hover:text-rose-600 ${
                      messageReactions[msg.id || msg.timestamp]?.disliked
                        ? "text-rose-600"
                        : "text-gray-500"
                    }`}
                    onClick={() =>
                      toggleMessageReaction(msg.id || msg.timestamp, "dislike")
                    }
                  >
                    <FaThumbsDown size={14} />
                  </button>
                  <button
                    type="button"
                    title="Share"
                    className="hover:text-indigo-600"
                    onClick={async () => {
                      const text = msg.text || "";
                      try {
                        if (navigator.share) {
                          await navigator.share({ text });
                        } else if (navigator.clipboard) {
                          await navigator.clipboard.writeText(text);
                          toast?.info?.("Copied to clipboard");
                        }
                      } catch (e) {
                        console.warn("Share failed", e);
                      }
                    }}
                  >
                    <FaShareAlt size={14} />
                  </button>
                </div>
              </div>
            ) : (
              <div
                key={`m-user-${msg.id || msg.timestamp || index}`}
                className="flex justify-end"
              >
                <div className="p-3 rounded-xl max-w-[90%] bg-indigo-50 text-gray-900 self-end">
                  {msg.questionImage && (
                    <div className="mb-2">
                      <div className="text-xs text-gray-600 mb-1">
                        Question:
                      </div>

                      <img
                        src={msg.questionImage}
                        alt="Question"
                        className="max-h-24 mb-2 rounded shadow border"
                      />
                    </div>
                  )}

                  {msg.image && (
                    <div className="mb-2">
                      <img
                        src={msg.image}
                        alt="Answer"
                        className="max-h-24 mb-2 rounded shadow border"
                      />
                    </div>
                  )}

                  <div>
                    <MathJaxRenderer className="whitespace-pre-line">
                      {msg.text}
                    </MathJaxRenderer>
                  </div>
                </div>
              </div>
            ),
          )}

          {showProcessingIndicator && (
            <SimpleThreeDotsLoader message={loadingMessage} />
          )}

          <div ref={chatEndRef}></div>
        </div>
      )}

      {/* Input Area */}

      <div className="relative mt-2 flex-shrink-0">
        {imagePreview && (
          <div className="mb-2 p-2 border border-gray-200 rounded-lg bg-gray-50 relative">
            <img
              src={imagePreview}
              alt="Preview"
              className="max-w-full rounded-lg"
              style={{ maxHeight: "100px" }}
            />

            <button
              onClick={onRemoveImage}
              className="absolute top-2 right-2 bg-gray-800 bg-opacity-50 text-white rounded-full p-1 hover:bg-opacity-70"
            >
              <FaTimes size={14} />
            </button>

            {imageName ? (
              <div className="text-xs text-gray-500 mt-1">{imageName}</div>
            ) : null}
          </div>
        )}

        {fileName && !imagePreview && (
          <div className="mb-2 p-2 border border-gray-200 rounded-lg bg-gray-50 relative">
            <div className="flex items-center">
              <FaPaperclip className="mr-2 text-indigo-500" />
              <span className="text-sm">{fileName}</span>
            </div>

            <button
              onClick={onClearFile}
              className="absolute top-2 right-2 bg-gray-800 bg-opacity-50 text-white rounded-full p-1 hover:bg-opacity-70"
            >
              <FaTimes size={14} />
            </button>
          </div>
        )}

        <div className="relative">
          <textarea
            ref={textareaRef}
            placeholder={
              fromQuestions
                ? "Your question is already loaded above. Click 'Get Step-by-Step Help' to start!"
                : "Enter Your Doubt"
            }
            value={inputText}
            onChange={(e) =>
              onInputTextChange && onInputTextChange(e.target.value)
            }
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            className={`w-full rounded-2xl border border-gray-300 px-4 py-3 pr-12 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
              fromQuestions
                ? "min-h-[100px] sm:min-h-[200px]"
                : "min-h-[60px] sm:min-h-[80px]"
            }`}
            disabled={isProcessing}
          />

          <div className="absolute bottom-3 left-4 flex gap-4 text-gray-500">
            <div className="relative flex items-center gap-2">
              <FaPlusCircle
                size={20}
                className={`cursor-pointer ${isProcessing ? "opacity-50" : ""}`}
                onClick={
                  !isProcessing ? () => setShowAttachMenu((v) => !v) : undefined
                }
              />

              {showAttachMenu && (
                <div className="absolute bottom-8 left-0 bg-white border border-gray-300 rounded-lg shadow-lg p-2 flex flex-col gap-2 z-10">
                  <label
                    className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 rounded cursor-pointer"
                    onClick={() => !isProcessing && setShowAttachMenu(false)}
                  >
                    <FaPaperclip size={16} /> Upload file
                    <input
                      key={`file-${resetKey}`}
                      type="file"
                      accept=".pdf,.doc,.docx,.txt"
                      onChange={onFileChange}
                      style={{ display: "none" }}
                      disabled={isProcessing}
                    />
                  </label>

                  <label
                    className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 rounded cursor-pointer"
                    onClick={() => !isProcessing && setShowAttachMenu(false)}
                  >
                    <FaImage size={16} /> Gallery
                    <input
                      key={`img-${resetKey}`}
                      type="file"
                      accept="image/*"
                      onChange={onImageChange}
                      style={{ display: "none" }}
                      disabled={isProcessing}
                    />
                  </label>

                  <button
                    onClick={handleCameraButtonClick}
                    className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 rounded"
                  >
                    <FaCamera size={16} />
                    Camera
                  </button>

                  {onWorkbookClick && !effectiveWorkbookMode && (
                    <button
                      onClick={() =>
                        !isProcessing &&
                        handleAndCloseAttachMenu(onWorkbookClick)
                      }
                      className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 rounded"
                    >
                      <FaBook size={16} />
                      Workbook
                    </button>
                  )}
                </div>
              )}
            </div>
            {/* New: Direct Worbook button beside plus */}
            <button
              onClick={() =>
                !isProcessing && handleAndCloseAttachMenu(onWorkbookClick)
              }
              className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 rounded"
            >
              <FaBook size={16} />
              Workbook
            </button>
            `
          </div>

          {supportsInternalCamera && (
            <MobileCameraModal
              isOpen={showCameraModal}
              onClose={handleCameraCloseInternal}
              onCapture={handleCameraCaptureInternal}
              hideCapture={cameraHideCapture}
              silenceTimerActive={silenceTimerActive}
              silenceTimerCount={silenceTimerCount}
              triggerCapture={triggerCameraCapture}
              onAutoCapture={onAutoCapture}
              handleSend={handleSend}
            />
          )}

          {/* Right-side controls: New Chat + Live Assist + Voice (mobile), replaces in-text-area Solve */}

          <div className="absolute bottom-3 right-3 flex flex-col items-end gap-1">
            {(onLiveAssistClick ||
              ((onVoiceClick || onResumeListening) && !isVoicePaused)) && (
              <div className="flex gap-2 items-center">
                {onLiveAssistClick && (
                  <span title="Live Assistance">
                    <img
                      src="/assets/video-camera-outline.jpg"
                      alt="Live Assistance"
                      width={24}
                      height={24}
                      style={{
                        marginRight: 0,
                        verticalAlign: "middle",
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        handleAndCloseAttachMenu(onLiveAssistClick)
                      }
                    />
                  </span>
                )}

                {onVoiceClick && (
                  <button
                    type="button"
                    className="flex items-center justify-center gap-1 px-4 py-1 rounded-full shadow transition-colors text-xs font-medium h-8 bg-gray-200 hover:bg-indigo-200 text-gray-700"
                    onClick={
                      !isProcessing && !showProcessingIndicator
                        ? onVoiceClick
                        : undefined
                    }
                    disabled={isProcessing || showProcessingIndicator}
                    title="Voice"
                    style={{ minWidth: 32 }}
                  >
                    <MdGraphicEq size={18} style={{ display: "block" }} />
                  </button>
                )}
              </div>
            )}

            {!showIntro &&
              Array.isArray(messages) &&
              messages.length > 0 &&
              onClearChat && (
                <div className="flex gap-2 items-center">
                  {!showIntro &&
                    Array.isArray(messages) &&
                    messages.length > 0 &&
                    onClearChat && (
                      <button
                        type="button"
                        onClick={() => onClearChat(false)}
                        className="flex items-center gap-2 px-3 py-1 rounded-full bg-primary text-white text-xs sm:text-sm whitespace-nowrap transition-colors hover:bg-primary-dark active:bg-primary-dark/90"
                        title="Clear all messages and start fresh"
                      >
                        <FaEdit className="w-4 h-4" />
                        {/* <span>New Chat</span> */}
                      </button>
                    )}
                </div>
              )}
          </div>
        </div>

        {renderCommunicationPanel ? (
          <div className="comm-panel-wrapper">
            {renderCommunicationPanel("mt-3")}
          </div>
        ) : null}

        {/* Mode/Actions */}

        <div
          className={`${
            showModeDropdownOnMobile
              ? "w-full flex flex-col gap-3 mt-4 flex-shrink-0"
              : "mt-4 flex items-center justify-between gap-2 flex-wrap"
          }`}
        >
          {showModeDropdownOnMobile ? (
            <>
              <div className="w-full">
                {/* First row: Back + Solve */}

                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex-1 min-w-[160px] flex gap-2">
                    {fromQuestions && (
                      <button
                        onClick={() =>
                          window.history ? window.history.back() : null
                        }
                        className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-bold rounded-full transition-colors text-sm"
                      >
                        Back to Quiz
                      </button>
                    )}

                    <button
                      className={`flex-1 px-4 py-2 ${
                        isProcessing
                          ? "bg-indigo-400"
                          : "bg-indigo-600 hover:bg-indigo-700"
                      } text-white font-bold rounded-full transition-colors text-sm`}
                      onClick={handleSendWithVoiceCleanup}
                      disabled={isProcessing}
                    >
                      {isProcessing
                        ? "Processing..."
                        : fromQuestions
                          ? "Get Step-by-Step Help"
                          : "Solve My Doubt"}
                    </button>
                  </div>
                </div>

                {/* Second row: Mode toggle */}

                <div className="mt-3">
                  <button
                    onClick={() =>
                      onModeChange && setShowMobileChatActions((v) => !v)
                    }
                    className="w-full min-w-[160px] flex items-center justify-between h-9 px-3 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-semibold rounded-full transition-colors text-sm"
                  >
                    <span>Mode</span>

                    <span className="text-xs">
                      {showMobileChatActions ? "\u25B2" : "\u25BC"}
                    </span>
                  </button>
                </div>

                {showMobileChatActions && (
                  <div className="mt-2 flex flex-col gap-2 border-2 border-indigo-700 rounded-[25px] p-2.5">
                    <div className="relative w-full ">
                      <button
                        onClick={() => handleModeToggle("homework")}
                        onMouseEnter={() => setShowHomeworkTooltip(true)}
                        onMouseLeave={() => setShowHomeworkTooltip(false)}
                        className={`flex w-full justify-center items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold transition-all ${
                          activeMode === "homework"
                            ? "border border-gray-400 bg-indigo-50 text-indigo-700"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        <FaBook />
                        Homework Help
                      </button>
                    </div>

                    <div className="relative w-full">
                      <button
                        onClick={() => handleModeToggle("stepbystep")}
                        onMouseEnter={() => setShowStepTooltip(true)}
                        onMouseLeave={() => setShowStepTooltip(false)}
                        className={`flex w-full justify-center items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold transition-all ${
                          activeMode === "stepbystep"
                            ? "border border-gray-400 bg-indigo-50 text-indigo-700"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        <FaPencil /> Step by Step Doubt Clearing
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="flex gap-2 relative">
                <button
                  onClick={() => handleModeToggle("homework")}
                  onMouseEnter={() => setShowHomeworkTooltip(true)}
                  onMouseLeave={() => setShowHomeworkTooltip(false)}
                  className={`flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2 rounded-full text-sm font-semibold transition-all ${
                    activeMode === "homework"
                      ? "border border-gray-400 bg-indigo-50 text-indigo-700"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <FaBook />
                  Homework Help
                </button>

                {showHomeworkTooltip && (
                  <div
                    className={`absolute ${
                      effectiveWorkbookMode
                        ? "bottom-full mb-2"
                        : "top-full mt-2"
                    } left-1/2 transform -translate-x-1/2 bg-gray-600 text-white text-xs  px-2 py-1 whitespace-nowrap z-50`}
                  >
                    Get Full Solution
                  </div>
                )}
              </div>

              <div className="relative">
                <button
                  onClick={() => handleModeToggle("stepbystep")}
                  onMouseEnter={() => setShowStepTooltip(true)}
                  onMouseLeave={() => setShowStepTooltip(false)}
                  className={`flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2 rounded-full text-sm font-semibold transition-all ${
                    activeMode === "stepbystep"
                      ? "border border-gray-400 bg-indigo-50 text-indigo-700"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <FaPencil /> Step by Step Doubt Clearing
                </button>

                {showStepTooltip && (
                  <div
                    className={`absolute ${
                      effectiveWorkbookMode
                        ? "bottom-full mb-2"
                        : "top-full mt-2"
                    } left-1/2 transform -translate-x-1/2 bg-gray-600 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-50`}
                  >
                    Get Step-by-Step Help
                  </div>
                )}
              </div>

              <div className="flex gap-2 sm:gap-4 mt-4 sm:mt-0">
                {fromQuestions && (
                  <button
                    onClick={() =>
                      window.history ? window.history.back() : null
                    }
                    className="px-4 py-2 sm:px-6 sm:py-3 bg-gray-600 hover:bg-gray-700 text-white font-bold rounded-full transition-colors text-sm"
                  >
                    Back to Quiz
                  </button>
                )}

                <button
                  className={`px-4 py-2 sm:px-6 sm:py-3 ${
                    isProcessing
                      ? "bg-indigo-400"
                      : "bg-indigo-600 hover:bg-indigo-700"
                  } text-white font-bold rounded-full transition-colors text-sm`}
                  onClick={handleSendWithVoiceCleanup}
                  disabled={isProcessing}
                >
                  {isProcessing
                    ? "Processing..."
                    : fromQuestions
                      ? "Get Step-by-Step Help"
                      : "Solve My Doubt"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  if (asModal) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex flex-col">
        <div className="flex items-center justify-between p-3 border-b">
          <div className="font-medium">{title}</div>

          <div className="flex items-center gap-2">
            {/* {effectiveWorkbookMode && hasActiveChat && (
              <button
                onClick={() => {
                  triggerClearChat();
                  onClose();
                }}
                className="p-2 rounded hover:bg-gray-100"
                aria-label="New Chat"
              >
                <FaEdit size={18} />
              </button>
            )} */}
            <button onClick={onClose} className="p-2 rounded hover:bg-gray-100">
              <FaTimes size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0">{content}</div>
      </div>
    );
  }

  return content;
};

export default MobileChatUI;
